import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Offcanvas } from 'bootstrap';
import { cleanupOffcanvasBackdrop } from '../../../utils/offcanvasHelpers';
import './RowActionMenu.scss';

/**
 * RowActionMenu — a kebab (⋮) dropdown for DataTable row actions.
 *
 * The menu is rendered through a portal to <body> and positioned with
 * `position: fixed`, so it always paints above the data table (which
 * otherwise clips / stacks over an in-row dropdown). Logic is unchanged:
 * each action's existing `onClick` is passed straight through.
 *
 * @param {Array<{
 *   label: string,
 *   icon?: string,            // icofont class, e.g. 'icofont-edit'
 *   onClick: (e) => void,
 *   variant?: 'default' | 'danger' | 'success',
 *   hidden?: boolean,
 *   disabled?: boolean,
 *   dataAttrs?: object   // extra data-* / aria-* attrs (e.g. Bootstrap triggers)
 * }>} actions
 * @param {'start' | 'end'} [align='end']   horizontal alignment vs the toggle
 */
const triggerBootstrapOverlay = (dataAttrs = {}) => {
  const toggle = dataAttrs['data-bs-toggle'];
  const target = dataAttrs['data-bs-target'];
  if (!toggle || !target) return;

  const el = document.querySelector(target);
  if (!el) return;

  if (toggle === 'offcanvas') {
    if (!el.classList.contains('show')) {
      cleanupOffcanvasBackdrop();
      Offcanvas.getOrCreateInstance(el).show();
    }
  } else if (toggle === 'modal') {
    Modal.getOrCreateInstance(el).show();
  }
};

function RowActionMenu({ actions = [], align = 'end' }) {
  const visible = actions.filter((a) => a && !a.hidden);

  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const positionMenu = useCallback(() => {
    const toggle = toggleRef.current;
    const menu = menuRef.current;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth || 180;
    const menuHeight = menu?.offsetHeight || 0;
    const gap = 6;

    // align menu's right edge to toggle's right (end) or left edge (start)
    let left = align === 'start' ? rect.left : rect.right - menuWidth;
    let top = rect.bottom + gap;

    // keep within viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left < 8) left = 8;
    if (left + menuWidth > vw - 8) left = vw - 8 - menuWidth;
    // flip above the toggle if it would overflow the bottom
    if (menuHeight && top + menuHeight > vh - 8) {
      top = Math.max(8, rect.top - gap - menuHeight);
    }

    setCoords({ top, left });
  }, [align]);

  // position once the menu is mounted, then on scroll/resize while open
  useLayoutEffect(() => {
    if (open) positionMenu();
  }, [open, positionMenu]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => positionMenu();
    const handleDocClick = (e) => {
      if (menuRef.current?.contains(e.target) || toggleRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, positionMenu]);

  if (!visible.length) return null;

  return (
    <div className="row-action-menu" onClick={(e) => e.stopPropagation()}>
      <button
        ref={toggleRef}
        type="button"
        className={`row-action-menu__toggle ${open ? 'is-open' : ''}`}
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <i className="bi bi-three-dots-vertical" aria-hidden="true" />
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="row-action-menu__menu"
              role="menu"
              style={{ top: coords.top, left: coords.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {visible.map((action, i) => {
                const dataAttrs = action.dataAttrs || {};
                const {
                  // 'data-bs-toggle': _toggle
                  'data-bs-target': _target,
                  ...restDataAttrs
                } = dataAttrs;

                return (
                  <button
                    key={i}
                    type="button"
                    role="menuitem"
                    disabled={action.disabled}
                    className={`row-action-menu__item row-action-menu__item--${action.variant || 'default'}`}
                    {...restDataAttrs}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick?.(e);
                      if (_target) {
                        window.requestAnimationFrame(() => {
                          triggerBootstrapOverlay(dataAttrs);
                        });
                      }
                      setOpen(false);
                    }}
                  >
                    {action.icon ? (
                      <i className={`${action.icon} row-action-menu__icon`} aria-hidden="true" />
                    ) : null}
                    <span>{action.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default RowActionMenu;
